import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';

import { NgModule, DEFAULT_CURRENCY_CODE } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { NgxDatatableModule } from '@swimlane/ngx-datatable';

import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { DashboardComponent } from './pages/dashboard/dashboard.component';
import { ClientDashboardComponent } from './pages/dashboard/client-dashboard/client-dashboard.component';
import { AdminDashboardComponent } from './pages/dashboard/admin-dashboard/admin-dashboard.component';
import { EngineerComponent } from './pages/administration/engineers/engineers.component';
import { EngineersManageComponent } from './pages/administration/engineers/engineers-manage/engineers-manage.component';
import { HTTP_INTERCEPTORS, HttpClientModule } from '@angular/common/http';
import { AccountService } from './services/account.service';
import { AccountPickerComponent } from './components/pickers/account-picker/account-picker.component';
import { CustomerPickerComponent } from './components/pickers/customer-picker/customer-picker.component';
import { CompanyPickerComponent } from './components/pickers/company-picker/company-picker.component';
import { CustomerService } from './services/customer.service';
import { AuthService } from './services/auth.service';
import { LoginComponent } from './pages/login/login.component';
import { SnotifyModule, SnotifyService, ToastDefaults } from 'ng-snotify';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule, DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MAT_MOMENT_DATE_FORMATS, MomentDateAdapter, MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { NgxMaterialTimepickerModule } from 'ngx-material-timepicker';

import { AssetRegisterComponent } from './pages/reports/asset-register/asset-register.component';
import { JobReportComponent } from './pages/reports/job-reports/job-reports.component';
import { ROTEReportsComponent } from './pages/reports/rote/rote-reports.component';
import { ReportableService } from './services/reportable.service';
import { WebusersComponent } from './pages/administration/webusers/webusers.component';
import { WebusersManageComponent } from './pages/administration/webusers/webusers-manage/webusers-manage.component';
import { WebUserService } from './services/webuser.service';
import { EngineerService } from './services/engineer.service';
import { UserProfilePickerComponent } from './components/pickers/user-profile-picker/user-profile-picker.component';
import { DialogModule } from 'primeng/dialog';
import { BasketComponent } from './components/basket/basket.component';
import { CompanyService } from './services/company.service';
import { AssetManagementComponent } from './pages/administration/asset-management/asset-management.component';
import { LocationService } from './services/location.service';
import { CategoryService } from './services/category.service';
import { EquipmentService } from './services/equipment.service';
import { InspectionService } from './services/inspection.service';
import { FilterService } from './services/filter.service';
import { CookieService } from 'ngx-cookie-service';
import { LocationPickerComponent } from './components/pickers/location-picker/location-picker.component';
import { CategoryPickerComponent } from './components/pickers/category-picker/category-picker.component';
import { AssetManageComponent } from './pages/administration/asset-management/asset-manage/asset-manage.component';
import { DescriptionPickerComponent } from './components/pickers/description-picker/description-picker.component';
import { ReportTypeService } from './services/reporttype.service';
import { ReportTypePickerComponent } from './components/pickers/report-type-picker/report-type-picker.component';
import { LoadingIconComponent } from './components/components/loading-icon/loading-icon.component';
import { SearchDropdown } from './components/components/searchable-dropdown/searchable-dropdown';
import { JobManagementComponent } from './pages/administration/job-management/job-management.component';
import { JobManageComponent } from './pages/administration/job-management/job-manage/job-manage.component';
import { JobService } from './services/job.service';
import { TechnicianPickerComponent } from './components/pickers/technician-picker/technician-picker.component';
import { TechnicianService } from './services/technician.service';
import { CalendarModule } from 'primeng/calendar';
import { CompanyManageComponent } from './pages/administration/companies/management/company-mange.component';
import { CompanyOverviewComponent } from './pages/administration/companies/overview/company-overview.component';
import { ModalService } from './services/modal.service';
import { PopUpModalComponent } from './components/components/popup-modal/popup-modal';
import { ManufacturerPickerComponent } from './components/pickers/manufacturer-picker/manufacturer-picker.component';
import { ManufacturerService } from './services/manufacturer.service';
import { IndividualInspectionComponent } from './pages/administration/inspections/individual-inspections/individual-inspections.component';
import { SublocationPickerComponent } from './components/pickers/sublocation-picker/sublocation-picker.component';
import { SublocationService } from './services/sublocation.service';
import { NgOtpInputModule } from 'ng-otp-input';
import { AuthInterceptor } from './services/auth.interceptor';
import { TextareaAutoresizeDirective } from './components/components/textarea-resize/textarea-resize';
import { UserDashboardComponent } from './pages/dashboard/user-dashboard/user-dashboard.component';
import { PurposePickerComponent } from './components/pickers/purpose-picker/purpose-picker.component';
import { PurposeService } from './services/purpose.service';
import { LocationComponent } from './pages/administration/locations/locations.component';
import { LocationsManageComponent } from './pages/administration/locations/locations-manage/locations-manage.component';
import { SublocationComponent } from './pages/administration/sublocations/sublocations.component';
import { UploadAssetComponent } from './pages/administration/asset-management/upload/upload.component';
import { ImportInspectionComponent } from './pages/import/inspections/import-inspections.component';

@NgModule({
  declarations: [
    //PAGES
    AppComponent,
    DashboardComponent,
    UserDashboardComponent,
    ClientDashboardComponent,
    AdminDashboardComponent,
    EngineerComponent,
    EngineersManageComponent,
    LoginComponent,
    UploadAssetComponent,
    WebusersComponent,
    WebusersManageComponent,
    ROTEReportsComponent,
    AssetManagementComponent,
    AssetManageComponent,
    AssetRegisterComponent,
    JobReportComponent,
    JobManagementComponent,
    JobManageComponent,
    CompanyOverviewComponent,
    IndividualInspectionComponent,
    CompanyManageComponent,
    LocationsManageComponent,
    SublocationComponent,
    LocationComponent,
    ImportInspectionComponent,
    //PICKERS
    AccountPickerComponent,
    CustomerPickerComponent,
    CompanyPickerComponent,
    DescriptionPickerComponent,
    LocationPickerComponent,
    SublocationPickerComponent,
    CategoryPickerComponent,
    ManufacturerPickerComponent,
    UserProfilePickerComponent,
    ReportTypePickerComponent,
    TechnicianPickerComponent,
    PurposePickerComponent,
    PopUpModalComponent,
    BasketComponent,
    LoadingIconComponent,
    SearchDropdown,
    TextareaAutoresizeDirective
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    HttpClientModule,
    NgSelectModule,
    FormsModule,
    NgxDatatableModule,
    SnotifyModule,
    BrowserAnimationsModule,
    MatDatepickerModule,
    MatNativeDateModule,
    NgxMaterialTimepickerModule,
    CalendarModule,
    NgOtpInputModule,
    ConfirmDialogModule,
    DialogModule
  ],
  providers: [
    AccountService,
    CustomerService,
    AuthService,
    SnotifyService,
    ModalService,
    ConfirmationService,
    ReportableService,
    WebUserService,
    EngineerService,
    CompanyService,
    LocationService,
    SublocationService,
    CategoryService,
    ManufacturerService,
    PurposeService,
    ReportTypeService,
    EquipmentService,
    InspectionService,
    JobService,
    TechnicianService,
    FilterService,
    CookieService,
     {
      provide : HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi   : true,
    },
    { provide: 'SnotifyToastConfig', useValue: ToastDefaults },
    { provide: MAT_DATE_LOCALE, useValue: 'en-GB' },
  ],

  bootstrap: [AppComponent]
})
export class AppModule { }
